<template>
  <v-app :class="{ 'theme--dark': isDarkMode }">
    <HeaderDealerDashboard
      :userName="userName"
      :businessName="businessName"
      :isDarkModeProp="isDarkMode"
      @navigate="handleNavigation"
    />

    <v-main class="dealer-main" :class="{ 'dark-mode': isDarkMode }">
      <v-container fluid class="pa-6">
        <!-- Page Header -->
        <div class="page-header mb-6">
          <v-row align="center">
            <v-col cols="12" md="6">
              <h1 class="text-h4 font-weight-bold">
                <span class="material-icons-round mr-2" style="vertical-align: middle;">inventory_2</span>
                Vehicle Inventory
              </h1>
              <p class="text-subtitle-1 grey--text mt-1">Manage your dealership's vehicle inventory</p>
            </v-col>
            <v-col cols="12" md="6" class="text-md-right">
              <v-btn color="orange" dark class="mr-2" @click="importFromWebsite" :loading="importingFromWebsite">
                <span class="material-icons-round mr-2">cloud_download</span>
                Import from Website
              </v-btn>
              <v-btn color="blue" dark class="mr-2" @click="openAddDialog">
                <span class="material-icons-round mr-2">add</span>
                Add Vehicle
              </v-btn>
              <v-btn color="success" outlined @click="loadInventory" :loading="loading">
                <span class="material-icons-round mr-2">refresh</span>
                Refresh
              </v-btn>
            </v-col>
          </v-row>
        </div>

        <!-- Stats Cards -->
        <v-row class="mb-6">
          <v-col cols="12" sm="6" md="3">
            <v-card class="stat-card" elevation="2">
              <v-card-text class="text-center">
                <span class="material-icons-round stat-icon primary--text">directions_car</span>
                <h3 class="text-h4 font-weight-bold mt-2">{{ totalCount }}</h3>
                <p class="text-subtitle-2 grey--text">Total Vehicles</p>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="stat-card" elevation="2">
              <v-card-text class="text-center">
                <span class="material-icons-round stat-icon success--text">check_circle</span>
                <h3 class="text-h4 font-weight-bold mt-2">{{ availableCount }}</h3>
                <p class="text-subtitle-2 grey--text">Available</p>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="stat-card" elevation="2">
              <v-card-text class="text-center">
                <span class="material-icons-round stat-icon warning--text">sell</span>
                <h3 class="text-h4 font-weight-bold mt-2">{{ soldCount }}</h3>
                <p class="text-subtitle-2 grey--text">Sold</p>
              </v-card-text>
            </v-card>
          </v-col>
          <v-col cols="12" sm="6" md="3">
            <v-card class="stat-card" elevation="2">
              <v-card-text class="text-center">
                <span class="material-icons-round stat-icon info--text">attach_money</span>
                <h3 class="text-h4 font-weight-bold mt-2">${{ avgPrice.toLocaleString() }}</h3>
                <p class="text-subtitle-2 grey--text">Avg. Price</p>
              </v-card-text>
            </v-card>
          </v-col>
        </v-row>

        <!-- Search and Filters -->
        <v-card class="mb-6" elevation="2">
          <v-card-text>
            <v-row align="center">
              <v-col cols="12" md="4">
                <v-text-field
                  v-model="search"
                  label="Search vehicles..."
                  outlined
                  dense
                  hide-details
                  clearable
                  @input="debouncedSearch"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="2">
                <v-select
                  v-model="filterBodyStyle"
                  :items="bodyStyleOptions"
                  label="Body Style"
                  outlined
                  dense
                  hide-details
                  clearable
                  @change="loadInventory"
                ></v-select>
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model="filterMinPrice"
                  label="Min Price"
                  type="number"
                  outlined
                  dense
                  hide-details
                  prefix="$"
                  @change="loadInventory"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="2">
                <v-text-field
                  v-model="filterMaxPrice"
                  label="Max Price"
                  type="number"
                  outlined
                  dense
                  hide-details
                  prefix="$"
                  @change="loadInventory"
                ></v-text-field>
              </v-col>
              <v-col cols="12" md="2">
                <v-btn color="grey" outlined block @click="clearFilters">
                  Clear Filters
                </v-btn>
              </v-col>
            </v-row>
          </v-card-text>
        </v-card>

        <!-- Inventory Table -->
        <v-card elevation="2">
          <v-data-table
            :headers="headers"
            :items="vehicles"
            :loading="loading"
            :options.sync="tableOptions"
            :server-items-length="totalCount"
            :footer-props="{ 'items-per-page-options': [10, 25, 50, 100] }"
            class="inventory-table"
            @update:options="onTableUpdate"
          >
            <template v-slot:item.image="{ item }">
              <v-avatar tile size="60" class="my-2 rounded">
                <v-img :src="getVehicleImage(item)" :alt="item.make + ' ' + item.model" cover></v-img>
              </v-avatar>
            </template>
            <template v-slot:item.vehicle="{ item }">
              <div class="font-weight-medium">{{ item.year }} {{ item.make }} {{ item.model }}</div>
              <div class="text-caption grey--text">{{ item.trim }}</div>
            </template>
            <template v-slot:item.amount="{ item }">
              <span class="font-weight-bold">${{ Number(item.amount || 0).toLocaleString() }}</span>
            </template>
            <template v-slot:item.odometer="{ item }">
              {{ Number(item.odometer || 0).toLocaleString() }} mi
            </template>
            <template v-slot:item.status="{ item }">
              <v-chip :color="item.is_sold_out ? 'error' : 'success'" small dark>
                {{ item.is_sold_out ? 'Sold' : 'Available' }}
              </v-chip>
            </template>
            <template v-slot:item.actions="{ item }">
              <v-btn icon small color="primary" @click="openEditDialog(item)" title="Edit">
                <v-icon small class="material-icons-round">edit</v-icon>
              </v-btn>
              <v-btn icon small color="error" @click="confirmDelete(item)" title="Delete">
                <v-icon small class="material-icons-round">delete</v-icon>
              </v-btn>
            </template>
          </v-data-table>
        </v-card>
      </v-container>
    </v-main>

    <FooterDealer />

    <!-- Add/Edit Vehicle Dialog -->
    <v-dialog v-model="vehicleDialog" max-width="1200" persistent scrollable>
      <v-card>
        <v-card-title class="blue white--text">
          <span class="material-icons-round mr-2">{{ editMode ? 'edit' : 'add' }}</span>
          {{ editMode ? 'Edit Vehicle' : 'Add New Vehicle' }}
          <v-spacer></v-spacer>
          <v-btn icon dark @click="closeVehicleDialog">
            <v-icon class="material-icons-round">close</v-icon>
          </v-btn>
        </v-card-title>
        <v-card-text class="pt-4" style="max-height: 75vh; overflow-y: auto;">
          <v-tabs v-model="formTab" background-color="transparent" color="primary" grow>
            <v-tab>Vehicle Info</v-tab>
            <v-tab>Price & Cost</v-tab>
            <v-tab v-if="editMode">Photos</v-tab>
          </v-tabs>
          <v-tabs-items v-model="formTab" class="mt-4">
            <!-- Vehicle Info Tab -->
            <v-tab-item>
              <v-form ref="vehicleForm" v-model="formValid">
                <v-row>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="vehicleForm.vin" label="VIN *" outlined dense :rules="[rules.required]" color="#e91e63"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="vehicleForm.stock_number" label="Stock Number" outlined dense color="#e91e63"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="vehicleForm.year" label="Year *" outlined dense type="number" :rules="[rules.required]" color="#e91e63"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="vehicleForm.make" label="Make *" outlined dense :rules="[rules.required]" color="#e91e63"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="vehicleForm.model" label="Model *" outlined dense :rules="[rules.required]" color="#e91e63"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-text-field v-model="vehicleForm.trim" label="Trim" outlined dense color="#e91e63"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="6">
                    <v-select v-model="vehicleForm.body_style" :items="bodyStyleOptions" label="Body Style" outlined dense color="#e91e63"></v-select>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="vehicleForm.odometer" label="Mileage" outlined dense type="number" suffix="mi" color="#e91e63"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select v-model="vehicleForm.transmission" :items="transmissionOptions" label="Transmission" outlined dense color="#e91e63"></v-select>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select v-model="vehicleForm.fuel_type" :items="fuelTypeOptions" label="Fuel Type" outlined dense color="#e91e63"></v-select>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="vehicleForm.exterior_color" label="Exterior Color" outlined dense color="#e91e63"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="vehicleForm.interior_color" label="Interior Color" outlined dense color="#e91e63"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="vehicleForm.engine" label="Engine" outlined dense color="#e91e63"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="vehicleForm.engine_power" label="Engine Power" outlined dense color="#e91e63"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="vehicleForm.cylinders" label="Cylinders" outlined dense color="#e91e63"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-select v-model="vehicleForm.drivetrain" :items="drivetrainOptions" label="Drivetrain" outlined dense color="#e91e63"></v-select>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="vehicleForm.wheel_drive" label="Wheel Drive" outlined dense color="#e91e63"></v-text-field>
                  </v-col>
                  <v-col cols="12" md="4">
                    <v-text-field v-model="vehicleForm.doors" label="Doors" outlined dense type="number" color="#e91e63"></v-text-field>
                  </v-col>
                </v-row>
              </v-form>
            </v-tab-item>
            <!-- Price & Cost Tab -->
            <v-tab-item>
              <v-row>
                <v-col cols="12" md="6">
                  <h5 class="text-h6 font-weight-bold mb-4">Price Info</h5>
                  <v-text-field v-model="vehicleForm.amount" label="Asking Price *" outlined dense type="number" prefix="$" :rules="[rules.required]" color="#e91e63"></v-text-field>
                  <v-text-field v-model="vehicleForm.special_price" label="Special Price" outlined dense type="number" prefix="$" color="#e91e63"></v-text-field>
                </v-col>
                <v-col cols="12" md="6">
                  <h5 class="text-h6 font-weight-bold mb-4">Cost Info</h5>
                  <v-text-field v-model="vehicleForm.vehicle_cost" label="Vehicle Cost" outlined dense type="number" prefix="$" color="#e91e63"></v-text-field>
                  <v-text-field v-model="vehicleForm.packs" label="Packs" outlined dense type="number" prefix="$" color="#e91e63"></v-text-field>
                  <v-text-field v-model="vehicleForm.total_tax" label="Total Tax" outlined dense type="number" prefix="$" color="#e91e63"></v-text-field>
                </v-col>
                <v-col cols="12">
                  <v-divider class="my-4"></v-divider>
                  <v-row>
                    <v-col cols="12" md="4">
                      <div class="text-center pa-4" style="background: #f5f5f5; border-radius: 8px;">
                        <p class="text-subtitle-2 grey--text mb-1">Total Cost</p>
                        <p class="text-h5 font-weight-bold primary--text">{{ currencyFormat(totalCost) }}</p>
                      </div>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="text-center pa-4" style="background: #f5f5f5; border-radius: 8px;">
                        <p class="text-subtitle-2 grey--text mb-1">Vehicle Price</p>
                        <p class="text-h5 font-weight-bold" style="color: #1867c0">{{ currencyFormat(vehicleForm.amount || 0) }}</p>
                      </div>
                    </v-col>
                    <v-col cols="12" md="4">
                      <div class="text-center pa-4" style="background: #f5f5f5; border-radius: 8px;">
                        <p class="text-subtitle-2 grey--text mb-1">Potential Profit</p>
                        <p class="text-h5 font-weight-bold" :style="{ color: potentialProfit >= 0 ? 'rgb(76, 175, 80)' : 'rgb(244, 67, 54)' }">{{ currencyFormat(potentialProfit) }}</p>
                      </div>
                    </v-col>
                  </v-row>
                </v-col>
              </v-row>
            </v-tab-item>
            <!-- Photos Tab -->
            <v-tab-item v-if="editMode">
              <v-row>
                <v-col cols="12">
                  <h5 class="text-h6 font-weight-bold mb-2">Photos ({{ vehicleImages.length }})</h5>
                  <v-btn color="primary" outlined class="mr-2 mb-4" @click="$refs.imageInput.click()" :loading="uploadingImages">
                    <span class="material-icons-round mr-2">add_photo_alternate</span>
                    Add Images
                  </v-btn>
                  <input type="file" ref="imageInput" multiple accept="image/*" style="display: none" @change="uploadImages">
                </v-col>
                <v-col cols="12">
                  <div class="d-flex flex-wrap">
                    <div v-for="(img, index) in vehicleImages" :key="img.id" class="image-container ma-2" style="position: relative;">
                      <v-img :src="img.image" width="150" height="150" cover class="rounded" style="cursor: pointer;"></v-img>
                      <v-btn icon small color="error" class="delete-btn" @click="deleteImage(img.id)" style="position: absolute; top: 5px; right: 5px;">
                        <v-icon small>close</v-icon>
                      </v-btn>
                    </div>
                    <div v-if="vehicleImages.length === 0" class="text-center pa-8 grey--text" style="width: 100%;">
                      <span class="material-icons-round" style="font-size: 64px;">photo_library</span>
                      <p class="mt-2">No images uploaded yet</p>
                    </div>
                  </div>
                </v-col>
              </v-row>
            </v-tab-item>
          </v-tabs-items>
        </v-card-text>
        <v-card-actions class="pa-4">
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="closeVehicleDialog">Cancel</v-btn>
          <v-btn color="blue" dark :loading="saving" :disabled="!formValid" @click="saveVehicle">
            {{ editMode ? 'Update' : 'Add' }} Vehicle
          </v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>

    <!-- Delete Confirmation Dialog -->
    <v-dialog v-model="deleteDialog" max-width="450">
      <v-card>
        <v-card-title class="error white--text">
          <v-icon dark class="mr-2 material-icons-round">warning</v-icon>
          Confirm Delete
        </v-card-title>
        <v-card-text class="pt-4">
          <p>Are you sure you want to delete this vehicle?</p>
          <p class="font-weight-bold" v-if="selectedVehicle">
            {{ selectedVehicle.year }} {{ selectedVehicle.make }} {{ selectedVehicle.model }}
          </p>
          <p class="text-caption error--text">This action cannot be undone.</p>
        </v-card-text>
        <v-card-actions>
          <v-spacer></v-spacer>
          <v-btn color="grey" text @click="deleteDialog = false">Cancel</v-btn>
          <v-btn color="error" :loading="deleting" @click="deleteVehicle">Delete</v-btn>
        </v-card-actions>
      </v-card>
    </v-dialog>
  </v-app>
</template>

<script>
import HeaderDealerDashboard from '@/components/Layout/HeaderDealerDashboard.vue'
import FooterDealer from '@/components/Layout/FooterDealer.vue'
import debounce from 'lodash/debounce'

export default {
  name: 'DealerInventory',
  components: {
    HeaderDealerDashboard,
    FooterDealer
  },
  data() {
    return {
      isDarkMode: true,
      loading: false,
      saving: false,
      deleting: false,
      uploadingImages: false,
      importingFromWebsite: false,
      userName: '',
      businessName: '',
      dealerId: null,
      dealerName: '',
      formTab: 0,
      vehicleImages: [],
      // Table data
      vehicles: [],
      totalCount: 0,
      availableCount: 0,
      soldCount: 0,
      avgPrice: 0,
      tableOptions: { page: 1, itemsPerPage: 10 },
      headers: [
        { text: 'Image', value: 'image', sortable: false, width: '80px' },
        { text: 'Vehicle', value: 'vehicle', sortable: false },
        { text: 'VIN', value: 'vin' },
        { text: 'Stock #', value: 'stock_number' },
        { text: 'Body Style', value: 'body_style' },
        { text: 'Price', value: 'amount' },
        { text: 'Mileage', value: 'odometer' },
        { text: 'Status', value: 'status', sortable: false },
        { text: 'Actions', value: 'actions', sortable: false, width: '120px' }
      ],
      // Filters
      search: '',
      filterBodyStyle: '',
      filterMinPrice: '',
      filterMaxPrice: '',
      bodyStyleOptions: ['Coupe', 'Convertible', 'Sedan', 'Hybrid/Electric', 'Crossover/SUV', 'Truck', '4WD/AWD', 'Van', 'Wagon', 'Hatchback'],
      transmissionOptions: ['Automatic', 'Manual', 'CVT'],
      fuelTypeOptions: ['Gasoline', 'Diesel', 'Electric', 'Hybrid', 'Plug-in Hybrid', 'CNG', 'LPG', 'Hydrogen', 'Ethanol', 'Biodiesel', 'Synthetic Fuels', 'Solar'],
      drivetrainOptions: ['FWD', 'RWD', 'AWD', '4WD'],
      // Dialogs
      vehicleDialog: false,
      deleteDialog: false,
      editMode: false,
      formValid: false,
      selectedVehicle: null,
      vehicleForm: this.getEmptyForm(),
      rules: {
        required: v => !!v || 'This field is required'
      }
    }
  },
  computed: {
    totalCost() {
      return Number(this.vehicleForm.vehicle_cost || 0) + Number(this.vehicleForm.packs || 0) + Number(this.vehicleForm.total_tax || 0)
    },
    potentialProfit() {
      return Number(this.vehicleForm.amount || 0) - this.totalCost
    }
  },
  created() {
    this.debouncedSearch = debounce(() => { this.loadInventory() }, 500)
  },
  beforeCreate() {
    if (!localStorage.getItem('token') || localStorage.getItem('user_flag') !== 'dealer') {
      this.$router.push('/dealers/login')
    }
  },
  async mounted() {
    this.loadDarkModePreference()
    this.loadUserData()
    await this.loadInventory()
  },
  methods: {
    getEmptyForm() {
      return {
        id: null, vin: '', stock_number: '', year: '', make: '', model: '', trim: '',
        body_style: '', amount: '', odometer: '', transmission: '', exterior_color: '',
        interior_color: '', engine: '', fuel_type: '', drivetrain: '', doors: '',
        engine_power: '', cylinders: '', wheel_drive: '',
        vehicle_cost: '', packs: '', total_tax: '', special_price: ''
      }
    },
    currencyFormat(value) {
      return '$' + Number(value || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
    },
    loadDarkModePreference() {
      const saved = localStorage.getItem('dealer_dark_mode')
      this.isDarkMode = saved !== null ? saved === 'true' : true
    },
    loadUserData() {
      this.userName = localStorage.getItem('user_name') || 'Dealer'
      this.businessName = localStorage.getItem('business_name') || 'Business'
      this.dealerId = localStorage.getItem('user_id')
      this.dealerName = localStorage.getItem('dealer_name') || this.businessName
    },
    async loadInventory() {
      try {
        this.loading = true
        const token = localStorage.getItem('token')
        const { page, itemsPerPage } = this.tableOptions
        const response = await axios.post('/api/dealer/get-inventory', {
          offset: (page - 1) * itemsPerPage,
          limit: itemsPerPage,
          search: this.search || '',
          filter_body_style: this.filterBodyStyle || '',
          amount_min: this.filterMinPrice || '',
          amount_max: this.filterMaxPrice || ''
        }, { headers: { 'Authorization': `Bearer ${token}` } })
        if (response.data.flag === 1) {
          this.vehicles = response.data.data || []
          this.totalCount = response.data.count || 0
          this.availableCount = response.data.available_count || 0
          this.soldCount = response.data.sold_count || 0
          this.avgPrice = response.data.avg_price || 0
        }
      } catch (error) {
        console.error('Error loading inventory:', error)
        this.$toast.error('Error loading inventory')
      } finally {
        this.loading = false
      }
    },
    onTableUpdate() {
      this.loadInventory()
    },
    clearFilters() {
      this.search = ''
      this.filterBodyStyle = ''
      this.filterMinPrice = ''
      this.filterMaxPrice = ''
      this.loadInventory()
    },
    getVehicleImage(item) {
      if (item.images && item.images.length > 0) {
        return item.images[0].image || item.images[0].image_url || item.images[0].url
      }
      return 'https://via.placeholder.com/60x60?text=No+Image'
    },
    handleNavigation(item) {},
    openAddDialog() {
      this.editMode = false
      this.formTab = 0
      this.vehicleForm = this.getEmptyForm()
      this.vehicleImages = []
      this.vehicleDialog = true
    },
    openEditDialog(item) {
      this.editMode = true
      this.formTab = 0
      this.vehicleForm = { ...item }
      this.vehicleImages = item.images || []
      this.vehicleDialog = true
    },
    closeVehicleDialog() {
      this.vehicleDialog = false
      this.formTab = 0
      this.vehicleForm = this.getEmptyForm()
      this.vehicleImages = []
    },
    async saveVehicle() {
      if (!this.$refs.vehicleForm.validate()) return
      try {
        this.saving = true
        const token = localStorage.getItem('token')
        const payload = { ...this.vehicleForm }
        const response = await axios.post('/api/dealer/save-vehicle', payload, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (response.data.flag === 1) {
          this.$toast.success(this.editMode ? 'Vehicle updated!' : 'Vehicle added!')
          // If new vehicle, update form with returned ID for image upload
          if (!this.editMode && response.data.vehicle_id) {
            this.vehicleForm.id = response.data.vehicle_id
            this.editMode = true
          }
          this.loadInventory()
        } else {
          this.$toast.error(response.data.message || 'Error saving vehicle')
        }
      } catch (error) {
        console.error('Error saving vehicle:', error)
        this.$toast.error('Error saving vehicle')
      } finally {
        this.saving = false
      }
    },
    async uploadImages(event) {
      const files = event.target.files
      if (!files || files.length === 0) return
      if (!this.vehicleForm.id) {
        this.$toast.error('Please save the vehicle first before uploading images')
        return
      }
      try {
        this.uploadingImages = true
        const token = localStorage.getItem('token')
        const formData = new FormData()
        for (let i = 0; i < files.length; i++) {
          formData.append('files[]', files[i])
        }
        const response = await axios.post(`/api/dealer/vehicle/${this.vehicleForm.id}/upload-images`, formData, {
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'multipart/form-data'
          }
        })
        if (response.data.flag === 1) {
          this.$toast.success('Images uploaded successfully!')
          await this.refreshVehicleImages()
        } else {
          this.$toast.error(response.data.message || 'Error uploading images')
        }
      } catch (error) {
        console.error('Error uploading images:', error)
        this.$toast.error('Error uploading images')
      } finally {
        this.uploadingImages = false
        this.$refs.imageInput.value = ''
      }
    },
    async refreshVehicleImages() {
      try {
        const token = localStorage.getItem('token')
        const response = await axios.get(`/api/dealer/vehicle/${this.vehicleForm.id}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (response.data.flag === 1) {
          this.vehicleImages = response.data.data.images || []
        }
      } catch (error) {
        console.error('Error refreshing images:', error)
      }
    },
    async deleteImage(imageId) {
      try {
        const token = localStorage.getItem('token')
        const response = await axios.delete(`/api/dealer/vehicle/image/${imageId}`, {
          headers: { 'Authorization': `Bearer ${token}` }
        })
        if (response.data.flag === 1) {
          this.$toast.success('Image deleted!')
          this.vehicleImages = this.vehicleImages.filter(img => img.id !== imageId)
        } else {
          this.$toast.error(response.data.message || 'Error deleting image')
        }
      } catch (error) {
        console.error('Error deleting image:', error)
        this.$toast.error('Error deleting image')
      }
    },
    confirmDelete(item) {
      this.selectedVehicle = item
      this.deleteDialog = true
    },
    async deleteVehicle() {
      try {
        this.deleting = true
        const token = localStorage.getItem('token')
        const response = await axios.post('/api/dealer/delete-vehicle', {
          ids: [{ id: this.selectedVehicle.id }]
        }, { headers: { 'Authorization': `Bearer ${token}` } })
        if (response.data.flag === 1) {
          this.$toast.success('Vehicle deleted!')
          this.deleteDialog = false
          this.selectedVehicle = null
          this.loadInventory()
        } else {
          this.$toast.error(response.data.message || 'Error deleting vehicle')
        }
      } catch (error) {
        console.error('Error deleting vehicle:', error)
        this.$toast.error('Error deleting vehicle')
      } finally {
        this.deleting = false
      }
    },
    async importFromWebsite() {
      try {
        this.importingFromWebsite = true
        const token = localStorage.getItem('token')

        // Step 1: Fetch inventory from autodealzaz
        const fetchResponse = await axios.post('/api/fetch-inventory-outside', {
          type: 'AutoDealzAZ',
          url: 'https://www.autodealzaz.com/cars-for-sale'
        }, { headers: { 'Authorization': `Bearer ${token}` } })

        if (fetchResponse.data && fetchResponse.data.data && fetchResponse.data.data.length > 0) {
          // Step 2: Save the fetched inventory with dealer's business name so it matches their inventory
          const saveResponse = await axios.post('/api/dealer/import-from-website', {
            inventory: fetchResponse.data.data
          }, { headers: { 'Authorization': `Bearer ${token}` } })

          if (saveResponse.data.flag === 1) {
            this.$toast.success(`Successfully imported ${fetchResponse.data.data.length} vehicles!`)
            this.loadInventory()
          } else {
            this.$toast.error(saveResponse.data.message || 'Error saving imported vehicles')
          }
        } else {
          this.$toast.warning('No vehicles found to import')
        }
      } catch (error) {
        console.error('Error importing from website:', error)
        this.$toast.error('Error importing vehicles from website')
      } finally {
        this.importingFromWebsite = false
      }
    }
  }
}
</script>

<style scoped>
.dealer-main {
  background-color: #f5f5f5;
  min-height: 100vh;
}
.dealer-main.dark-mode {
  background: url('~@/assets/img/skyline-bg-dark.png') no-repeat center bottom;
  background-size: cover;
  background-color: #0f0f23;
}

.stat-card {
  border-radius: 12px !important;
}

.stat-icon {
  font-size: 48px !important;
}

.inventory-table {
  border-radius: 12px !important;
}

.v-card {
  border-radius: 12px !important;
}

.image-container {
  position: relative;
  display: inline-block;
}

.image-container .delete-btn {
  opacity: 0;
  transition: opacity 0.2s ease;
  background: rgba(255, 255, 255, 0.9) !important;
}

.image-container:hover .delete-btn {
  opacity: 1;
}
</style>

<style>
/* Dark mode styles - unscoped for proper cascading */
.dark-mode .stat-card,
.dark-mode .v-card,
.dark-mode .inventory-table {
  background: rgba(30, 30, 60, 0.8) !important;
  border: 1px solid rgba(138, 43, 226, 0.3);
}
/* Text colors */
.dark-mode .v-card-title,
.dark-mode .v-card-text,
.dark-mode .v-card-text *,
.dark-mode .v-data-table,
.dark-mode .v-data-table th,
.dark-mode .v-data-table td,
.dark-mode .v-list-item__title,
.dark-mode .v-list-item__subtitle,
.dark-mode .v-list-item__content,
.dark-mode .stat-value,
.dark-mode .stat-label,
.dark-mode .text-h4,
.dark-mode .text-h5,
.dark-mode .text-h6,
.dark-mode .text-subtitle-1,
.dark-mode .text-subtitle-2,
.dark-mode .text-body-1,
.dark-mode .text-body-2,
.dark-mode .text-caption,
.dark-mode p,
.dark-mode span:not(.v-btn__content span):not(.v-chip__content),
.dark-mode strong,
.dark-mode label,
.dark-mode .v-label,
.dark-mode .v-messages__message,
.dark-mode .v-input__slot,
.dark-mode .v-text-field input,
.dark-mode .v-textarea textarea,
.dark-mode .v-select__selection,
.dark-mode .v-radio .v-label,
.dark-mode .v-checkbox .v-label,
.dark-mode .v-switch .v-label {
  color: #e0e0e0 !important;
}
/* Icons */
.dark-mode .v-icon,
.dark-mode .material-icons,
.dark-mode .material-icons-round,
.dark-mode .material-icons-outlined {
  color: #e0e0e0 !important;
}
/* Button icons should keep their color */
.dark-mode .v-btn .v-icon,
.dark-mode .v-btn .material-icons,
.dark-mode .v-btn .material-icons-round {
  color: inherit !important;
}
/* Colored text overrides */
.dark-mode .v-data-table__empty-wrapper {
  color: rgba(255,255,255,0.5) !important;
}
.dark-mode .v-card-title.primary--text {
  color: #42a5f5 !important;
}
.dark-mode .primary--text {
  color: #42a5f5 !important;
}
</style>
